import React, { useEffect, useRef, useState } from 'react'
import {
    AvailableAdGroup,
    Destination,
    DestinationMatchType,
    PromotionStructure,
    Source,
    defaultStructure,
} from './mockData'
import { AdGroupPicker } from './AdGroupPicker'
import { BulkEditIcon, MatrixModal } from './MatrixModal'
import {
    MatchBadge,
    MT_LABEL,
    badgeTypeFor,
    deriveStatus,
    eligibleNegationKeys,
    eligibleTargetKeys,
} from './sharedRoutingHelpers'
import './routing-cards.css'
import { Popover } from '../../ui/popover'

interface SingleCardPrototypeProps {
    structure?: PromotionStructure
}

/**
 * Wraps a scrollable list and tracks whether it's scrolled to the bottom.
 * The bottom-fade mask is suppressed when at-bottom so the last item
 * doesn't get visually clipped, but the fade reappears the moment the
 * user scrolls up.
 */
export const ScrollList = ({
    scrollable,
    className,
    children,
}: {
    scrollable: boolean
    className: string
    children: React.ReactNode
}) => {
    const ref = useRef<HTMLDivElement>(null)
    const [atBottom, setAtBottom] = useState(false)
    const [atTop, setAtTop] = useState(true)

    useEffect(() => {
        if (!scrollable) return
        const el = ref.current
        if (!el) return
        const check = () => {
            const remaining = el.scrollHeight - el.scrollTop - el.clientHeight
            setAtBottom(remaining <= 1)
            setAtTop(el.scrollTop <= 0)
        }
        check()
        el.addEventListener('scroll', check, { passive: true })
        const ro = new ResizeObserver(check)
        ro.observe(el)
        return () => {
            el.removeEventListener('scroll', check)
            ro.disconnect()
        }
    }, [scrollable, children])

    return (
        <div
            ref={ref}
            className={
                className +
                (scrollable ? ' is-scrollable' : '') +
                (scrollable && atBottom ? ' is-at-bottom' : '') +
                (scrollable && atTop ? ' is-at-top' : '')
            }
        >
            {children}
        </div>
    )
}

const XIcon = () => (
    <svg viewBox="0 0 10 10" width="9" height="9" aria-hidden="true">
        <path
            d="M2 2 L8 8 M8 2 L2 8"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
        />
    </svg>
)

function NamePopover({
    children,
    className,
    targetTagName = 'span',
}: {
    children: string
    className: string
    targetTagName?: keyof JSX.IntrinsicElements
}) {
    return (
        <Popover
            content={<div className="pm-route-name-popover-content">{children}</div>}
            interactionKind="hover"
            position="top"
            minimal
            hoverOpenDelay={250}
            className={className}
            targetTagName={targetTagName}
        >
            {children}
        </Popover>
    )
}

interface DestRowProps {
    dest: Destination
    lane: 'promote' | 'negate'
    onToggleMatch: (m: DestinationMatchType) => void
    onRemove: () => void
}

export const DestRow = ({ dest, lane, onToggleMatch, onRemove }: DestRowProps) => {
    const matchType = dest.type.toLowerCase()
    const keys =
        lane === 'promote' ? eligibleTargetKeys(matchType) : eligibleNegationKeys(matchType)
    const status = deriveStatus(dest.id)
    const variant = lane === 'promote' ? 'tgt' : 'neg'
    return (
        <div className="pm-route-dest group/card">
            <div className="pm-route-destname">
                <span className={`status-dot status-dot--${status}`} />
                <NamePopover className="pm-route-destname-text">{dest.adGroupName}</NamePopover>
                <MatchBadge type={badgeTypeFor(dest.type)} />
                <button
                    type="button"
                    className="pm-chip-x pm-route-dest-x opacity-0 group-hover/card:opacity-100 transition-opacity"
                    onClick={onRemove}
                    aria-label="Remove"
                >
                    <XIcon />
                </button>
            </div>
            <NamePopover className="pm-route-destcamp" targetTagName="div">
                {dest.campaignName}
            </NamePopover>
            <div className="pm-route-mts">
                {keys.map((k) => {
                    const on = dest.matchTypes.includes(k as DestinationMatchType)
                    return (
                        <button
                            key={k}
                            type="button"
                            className={`pm-pill pm-pill--${variant}` + (on ? ' pm-pill--on' : '')}
                            onClick={() => onToggleMatch(k as DestinationMatchType)}
                        >
                            {MT_LABEL[k]}
                        </button>
                    )
                })}
            </div>
        </div>
    )
}

interface SourceRowProps {
    source: Source
    onRemove: () => void
}

export const SourceRow = ({ source, onRemove }: SourceRowProps) => {
    const status = deriveStatus(source.id)
    return (
        <div className="pm-route-srcrow group/card">
            <div className="pm-route-srcrow-main">
                <div className="pm-route-srcname">
                    <span className={`status-dot status-dot--${status}`} />
                    <NamePopover className="pm-route-destname-text">
                        {source.displayName ?? source.adGroupName}
                    </NamePopover>
                    <MatchBadge type={badgeTypeFor(source.type)} />
                </div>
                <NamePopover className="pm-route-srccamp" targetTagName="div">
                    {source.asin ? `${source.asin} · ${source.campaignName}` : source.campaignName}
                </NamePopover>
            </div>
            <button
                type="button"
                className="pm-chip-x pm-route-src-x opacity-0 group-hover/card:opacity-100 transition-opacity"
                onClick={onRemove}
                aria-label="Remove source"
            >
                <XIcon />
            </button>
        </div>
    )
}

export const SingleCardPrototype = ({ structure = defaultStructure }: SingleCardPrototypeProps) => {
    const [sources, setSources] = useState<Source[]>(structure.sources)
    const [promote, setPromote] = useState<Destination[]>(structure.promote)
    const [negate, setNegate] = useState<Destination[]>(structure.negate)
    const [picker, setPicker] = useState<{ kind: 'src' | 'tgt' | 'neg' } | null>(null)
    const [bulkOpen, setBulkOpen] = useState(false)

    const SCROLL_AT = 4

    const handlePick = (g: AvailableAdGroup) => {
        if (!picker) return
        if (picker.kind === 'src') {
            const next: Source = {
                id: g.id,
                campaignName: g.campaignName,
                adGroupName: g.adGroupName,
                displayName: g.adGroupName,
                type: g.type,
                ...(g.asin ? { asin: g.asin } : {}),
            }
            setSources([...sources, next])
        } else {
            const next: Destination = {
                id: g.id,
                campaignName: g.campaignName,
                adGroupName: g.adGroupName,
                type: g.type,
                matchTypes: [],
            }
            if (picker.kind === 'tgt') setPromote([...promote, next])
            else setNegate([...negate, next])
        }
        setPicker(null)
    }

    const excludeIds =
        picker?.kind === 'src'
            ? sources.map((s) => s.id)
            : picker?.kind === 'tgt'
              ? promote.map((d) => d.id)
              : picker?.kind === 'neg'
                ? negate.map((d) => d.id)
                : []

    const toggleMatch = (lane: 'promote' | 'negate', id: string, m: DestinationMatchType) => {
        const list = lane === 'promote' ? promote : negate
        const setter = lane === 'promote' ? setPromote : setNegate
        setter(
            list.map((d) =>
                d.id === id
                    ? {
                          ...d,
                          matchTypes: d.matchTypes.includes(m)
                              ? d.matchTypes.filter((x) => x !== m)
                              : [...d.matchTypes, m],
                      }
                    : d,
            ),
        )
    }

    const removeSource = (id: string) => {
        setSources(sources.filter((s) => s.id !== id))
    }

    const removeDestination = (lane: 'promote' | 'negate', id: string) => {
        if (lane === 'promote') {
            setPromote(promote.filter((d) => d.id !== id))
        } else {
            setNegate(negate.filter((d) => d.id !== id))
        }
    }

    return (
        <div className="routing-cards-root">
            <div className="pm-route">
                <div className="pm-route-card">
                    <div className="pm-route-header">
                        <div className="pm-route-src">
                            <div className="pm-route-label">
                                {sources.length > 1 ? 'SOURCES' : 'SOURCE'}
                            </div>
                            {sources.length === 0 ? (
                                <div className="pm-route-empty-line">No sources yet</div>
                            ) : (
                                <ScrollList
                                    scrollable={sources.length > SCROLL_AT}
                                    className={
                                        'pm-route-srclist' +
                                        (sources.length > SCROLL_AT
                                            ? ' pm-route-srclist--scroll'
                                            : '')
                                    }
                                >
                                    {sources.map((src) => (
                                        <SourceRow
                                            key={src.id}
                                            source={src}
                                            onRemove={() => removeSource(src.id)}
                                        />
                                    ))}
                                </ScrollList>
                            )}
                            <button
                                type="button"
                                className="pm-route-addbtn pm-route-addbtn--src"
                                onClick={() => setPicker({ kind: 'src' })}
                            >
                                {sources.length === 0
                                    ? '+ Add source ad group'
                                    : '+ Add another source'}
                            </button>
                        </div>
                        <button
                            type="button"
                            className="pm-matrix-open-btn"
                            onClick={() => setBulkOpen(true)}
                        >
                            <BulkEditIcon />
                            Bulk edit
                        </button>
                    </div>
                    <div className="pm-route-body">
                        <div className="pm-route-col pm-route-col--tgt">
                            <div className="pm-route-coltitle">
                                <span className="pm-bullet pm-bullet--tgt" />
                                Promote matches to
                            </div>
                            {promote.length === 0 && (
                                <div className="pm-route-empty-line">No targets yet</div>
                            )}
                            <ScrollList
                                scrollable={promote.length > SCROLL_AT}
                                className={
                                    'pm-route-destlist' +
                                    (promote.length > SCROLL_AT ? ' pm-route-destlist--scroll' : '')
                                }
                            >
                                {promote.map((t) => (
                                    <DestRow
                                        key={t.id}
                                        dest={t}
                                        lane="promote"
                                        onToggleMatch={(m) => toggleMatch('promote', t.id, m)}
                                        onRemove={() => removeDestination('promote', t.id)}
                                    />
                                ))}
                            </ScrollList>
                            <button
                                type="button"
                                className="pm-route-addbtn pm-route-addbtn--tgt"
                                onClick={() => setPicker({ kind: 'tgt' })}
                            >
                                + Add target
                            </button>
                        </div>
                        <div className="pm-route-col pm-route-col--neg">
                            <div className="pm-route-coltitle">
                                <span className="pm-bullet pm-bullet--neg" />
                                Negate matches in
                            </div>
                            {negate.length === 0 && (
                                <div className="pm-route-empty-line">No negations yet</div>
                            )}
                            <ScrollList
                                scrollable={negate.length > SCROLL_AT}
                                className={
                                    'pm-route-destlist' +
                                    (negate.length > SCROLL_AT ? ' pm-route-destlist--scroll' : '')
                                }
                            >
                                {negate.map((t) => (
                                    <DestRow
                                        key={t.id}
                                        dest={t}
                                        lane="negate"
                                        onToggleMatch={(m) => toggleMatch('negate', t.id, m)}
                                        onRemove={() => removeDestination('negate', t.id)}
                                    />
                                ))}
                            </ScrollList>
                            <button
                                type="button"
                                className="pm-route-addbtn pm-route-addbtn--neg"
                                onClick={() => setPicker({ kind: 'neg' })}
                            >
                                + Add negation
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            {picker && (
                <AdGroupPicker
                    kind={picker.kind}
                    excludeIds={excludeIds}
                    onPick={handlePick}
                    onClose={() => setPicker(null)}
                />
            )}
            {bulkOpen && (
                <MatrixModal
                    sources={sources}
                    promote={promote}
                    negate={negate}
                    onSave={(next) => {
                        setSources(next.sources)
                        setPromote(next.promote)
                        setNegate(next.negate)
                        setBulkOpen(false)
                    }}
                    onClose={() => setBulkOpen(false)}
                />
            )}
        </div>
    )
}
